import { useEffect, useState } from 'react';
import { Button, ButtonGroup, TextField, Typography, Chip, Box } from '@mui/material';
import { useUpdatePropertyMutation } from '../../store/docApi';
import { renderIconSvg } from './iconsSvg';
import ColorwayLegend from '../../Colorway/ColorwayLegend';

export default function ColorChart({ palette, thumbnail, item, depth, viewonly, progress, updateProgress, colorway }) {
  const [rows, setRows] = useState(item?.data?.length || 0);
  const [cols, setCols] = useState(item?.data?.[0]?.length || 0);
  const [data, setData] = useState(item?.data || []);
  const [edit, setEdit] = useState(false);
  const defaultColors = ['white', 'grey', 'lightpink', 'magenta', 'lightblue', 'lightgreen', 'yellow', 'orange', 'purple', 'cyan', 'red', 'blue', 'green', 'black', 'brown'];
  const [colors, setColors] = useState(item?.colors || defaultColors);
  const defaultIcons = ['circle', 'square', 'triangle', 'star', 'diamond', 'x', 'arrow', 'arrowUp', 'arrowDown', 'arrowLeft', 'chevronRight', 'chevronLeft', 'heart', 'cross', 'dot'];
  const [icons, setIcons] = useState(item?.icons || defaultIcons);
  const [color, setColor] = useState(1);
  const [verticalLines] = useState(item?.settings?.verticallines || []);
  const [mouseDown, setMouseDown] = useState(false);

  const [updateProperty, result] = useUpdatePropertyMutation();

  useEffect(() => {
    if (colorway && colorway.chromas) {
      const colors = colorway.chromas.map((chroma) => chroma.color);
      if (colors.length > 0) {
        setColors(colors);
      }
      const icons = colorway.chromas.map((chroma) => chroma.icon);
      if (icons.length > 0) {
        setIcons(icons);
      }
    }
  }, [colorway]);

  const onSaveData = async () => {
    await updateProperty({ label: item.label, pk: item.pk, id: item.id, name: 'data', value: data });
    setEdit(false);
  };

  const onSaveColors = async () => {
    await updateProperty({ label: item.label, pk: item.pk, id: item.id, name: 'colors', value: colors });
    setEdit(false);
  };

  const onSaveIcons = async () => {
    await updateProperty({ label: item.label, pk: item.pk, id: item.id, name: 'icons', value: icons });
    setEdit(false);
  };

  async function setCurrentRow(num) {
    if (num >= item.data.length) num = -1;
    if (updateProgress) await updateProgress(`${depth}_row`, num);
  }

  function getCurrentRow() {
    return progress && progress[`${depth}_row`] !== undefined ? progress[`${depth}_row`] : -1;
  }

  function getCellColor(col) {
    // Handle no-stitch cells
    if (col === null) {
      return item?.settings?.noStitchColor || '#e0e0e0'; // Use configurable color or default
    }
    if (palette && palette.shades && palette.shades[col]) {
      return palette.shades[col].color;
    }
    return colors[col] || '#f3f3f3'; // Default color if colors[col] is undefined
  }

  function isSolid(col) {
    return colorway && colorway.chromas && colorway.chromas.length > col && colorway.chromas[col].isSolid;
  }

  function setValue(i, j, val) {
    let newData = JSON.parse(JSON.stringify(data));
    newData[i][j] = val;
    setData(newData);
  }

  function updateGrid() {
    const newData = new Array(rows).fill(0).map(() => new Array(cols).fill(0));
    for (var i = 0; i < newData.length && i < data.length; i++)
      for (var j = 0; j < newData[0].length && j < data[0].length; j++) {
        newData[i][j] = data[i][j];
      }
    setData(newData);
  }

  function copyData(data) {
    const formattedData = '[' + data.map((row) => '[' + row.join(', ') + ']').join(',\n') + ']';
    navigator.clipboard
      .writeText(formattedData)
      .then(() => {
        console.log('Data copied to clipboard', formattedData);
      })
      .catch((err) => {
        console.error('Could not copy text: ', err);
      });
  }

  let split = item && item.metadata && item.metadata.split ? item.metadata.split : 0;

  return data ? (
    <div>
      {!thumbnail && !viewonly && (
        <div style={{ padding: 5 }}>
          <div>
            {cols} x {rows}
          </div>
          <Button variant="outlined" disabled={edit} onClick={() => setEdit(true)}>
            Edit
          </Button>
          &nbsp;&nbsp;
          <Button variant="outlined" disabled={edit} onClick={() => copyData(item.data)}>
            Copy
          </Button>
        </div>
      )}
      {result && result.isError && <Typography color="error">Error saving</Typography>}
      {edit && (
        <Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Typography>Cols:</Typography>
            <TextField
              type="number"
              value={cols}
              inputProps={{ min: 1, max: 100 }}
              onChange={(e) => setCols(Number(e.target.value))}
              sx={{ width: 80, ml: 2 }}
              size="small"
            />
            <Typography sx={{ ml: 2 }}>Rows:</Typography>
            <TextField
              type="number"
              value={rows}
              inputProps={{ min: 1, max: 100 }}
              onChange={(e) => setRows(Number(e.target.value))}
              sx={{ width: 80, ml: 2 }}
              size="small"
            />
            <Button variant="outlined" onClick={() => updateGrid()} sx={{ ml: 2 }}>
              Update
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 2 }}>
            <Typography>Colors:</Typography>
            <Chip variant="outlined" onDelete={() => {}} sx={{ ml: 2 }} onClick={() => {}} />
            <Button disabled={!edit} onClick={() => onSaveColors()} sx={{ ml: 2 }}>
              Save
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 2 }}>
            <Typography>Icons:</Typography>
            <Chip variant="outlined" onClick={() => {}} sx={{ ml: 2 }} />
            <Button disabled={!edit} onClick={() => onSaveIcons()} sx={{ ml: 2 }}>
              Save
            </Button>
          </Box>
          <ButtonGroup sx={{ mt: 2 }}>
            {colors.map((color, i) => (
              <Button
                key={i}
                variant={color === i ? 'contained' : 'outlined'}
                onClick={() => setColor(i)}
                sx={{ backgroundColor: colors[i] }}
              >
                {i}
              </Button>
            ))}
            <Button onClick={() => onSaveData()}>Save</Button>
          </ButtonGroup>
        </Box>
      )}
      <br />
      <div style={styles.chartContainer}>
        {!thumbnail && !edit && cols && rows && split > 0 && (
          <div style={styles.horizontalDecor}>
            {Array(split)
              .fill({})
              .map((s, i) => (
                <div key={i} style={{ ...styles.section, height: rows * 16, width: (cols / split) * 16 }} />
              ))}
          </div>
        )}
        {!thumbnail && !edit && cols && rows && (
          <div style={styles.verticalDecor}>
            {data.map((step, i) =>
              getCurrentRow() === data.length - i - 1 ? (
                <div
                  key={i}
                  style={{ ...styles.rowHighlight, cursor: 'pointer', height: 16, width: cols * 16 }}
                  onClick={() => setCurrentRow(-i)}
                />
              ) : (
                <div
                  key={i}
                  style={{ cursor: 'pointer', height: 16, width: cols * 16 }}
                  onClick={() => setCurrentRow(data.length - i - 1)}
                />
              )
            )}
          </div>
        )}
        <div style={styles.horizontal}>
          <div style={styles.chart}>
            {!thumbnail && (
              <div style={styles.step}>
                {data[0] ? (
                  data[0].map((col, j) => (
                    <span key={j} style={styles.cellnum}>
                      {data[0].length - j}
                    </span>
                  ))
                ) : (
                  <div />
                )}
              </div>
            )}
            {data.map((step, i) => (
              <div key={i} style={styles.step}>
                {step.map((col, j) => {
                  const cellColor = getCellColor(col);
                  const icon = icons[col % icons.length];
                  return thumbnail ? (
                    <div key={j} style={{ ...styles.cellsmall, backgroundColor: cellColor }} />
                  ) : (
                    <div
                      key={j}
                      style={{
                        ...styles.cell,
                        backgroundColor: cellColor,
                        borderLeft: verticalLines.includes(j) ? '4px double red' : '1px solid'
                      }}
                      onMouseDown={() => setValue(i, j, color)}
                      onMouseUp={() => setMouseDown(false)}
                      onMouseEnter={() => mouseDown && setValue(i, j, color)}
                      onClick={() => setValue(i, j, color)}
                    >
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 10 10"
                        style={{ display: 'block', margin: 'auto', transform: 'translate(0, 2px)' }}
                      >
                        {renderIconSvg(icon, cellColor, isSolid(col))}
                      </svg>
                    </div>
                  );
                })}
                {!thumbnail && <span style={styles.cellnum}>{data.length - i}</span>}
              </div>
            ))}
            {!thumbnail && (
              <div style={styles.step}>
                {data[0] ? (
                  data[0].map((col, j) => (
                    <span key={j} style={styles.cellnum}>
                      {data[0].length - j}
                    </span>
                  ))
                ) : (
                  <div />
                )}
              </div>
            )}
          </div>
          {false && colorway && (
            <div style={{ paddingLeft: 32 }}>
              {' '}
              <ColorwayLegend colorway={colorway} />
            </div>
          )}
        </div>
      </div>
      {viewonly && updateProgress && (
        <Button style={styles.nextButton} onClick={() => setCurrentRow(getCurrentRow() + 1)}>
          Next Row
        </Button>
      )}
    </div>
  ) : (
    <span>no data?</span>
  );
}

const styles = {
  nextButton: {
    margin: 8
  },
  horizontal: {
    display: 'flex',
    flexDirection: 'row'
  },
  chartContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    overflowX: 'auto',
    backgroundColor: 'white'
  },
  horizontalDecor: {
    position: 'absolute',
    display: 'flex',
    direction: 'row'
  },
  section: {
    borderColor: 'darkslategrey',
    borderStyle: 'solid',
    borderRightWidth: 1,
    borderTopWidth: 0,
    borderBottomWidth: 0
  },
  rowHighlight: {
    borderColor: 'gold',
    borderStyle: 'solid',
    borderWidth: 2
  },
  verticalDecor: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column'
  },
  chart: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap'
  },
  step: {
    display: 'flex',
    flexDirection: 'row',
    alignSelf: 'flex-start'
  },
  cellsmall: {
    height: 4,
    width: 4,
    borderWidth: 0,
    borderColor: 'black',
    borderStyle: 'solid'
  },
  cell: {
    height: 16,
    width: 16,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0, 0.3)',
    borderStyle: 'solid',
    position: 'relative'
  },
  cellnum: {
    height: 16,
    width: 16,
    fontSize: 10,
    textAlign: 'center',
    cursor: 'pointer'
  },
  styleRS: {
    flexDirection: 'row-reverse',
    alignSelf: 'flex-end'
  },
  styleWS: {
    flexDirection: 'row',
    alignSelf: 'flex-end'
  },
  chartsquareblank: {
    width: 18,
    height: 18,
    fontSize: 12,
    textAlign: 'center',
    color: 'blueviolet'
  },
  chartsquaretext: {
    position: 'absolute',
    color: 'red',
    fontSize: 10
  },
  charticon: {
    width: '100%',
    height: '100%'
  },
  control: {
    marginLeft: 10
  }
};
